import JsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    logoFull4x,
    logoUNWomenBluePNG,
    logoForAllWomenAndGirlsPNG
} from '../assets';
import { COLOR_05, COLOR_12, FONT_WEIGHT_REGULAR } from '../constants';
import { getLargeDate } from '../utils';

interface PDFTableData {
    title: string;
    subtitle?: string;
    headers: string[];
    rows: (string | number)[][];
    columnSizePercentages?: number[];
}

const createPDF = (): JsPDF => {
    const pdfInstance = new JsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'letter',
        putOnlyUsedFonts: true
    });

    return pdfInstance;
};

const addPDFHeader = (
    pdfInstance: JsPDF,
    dataDate: string
): number => {
    pdfInstance.addImage(
        logoFull4x, 'PNG',
        10, 8,
        48, 0
    );
    pdfInstance.addImage(
        logoUNWomenBluePNG, 'PNG',
        163, 7.9,
        20, 0
    );
    pdfInstance.addImage(
        logoForAllWomenAndGirlsPNG, 'PNG',
        186, 8,
        17, 0
    );
    pdfInstance.setFont('Helvetica');
    pdfInstance.setFontSize(8);
    pdfInstance.setTextColor(COLOR_05);
    pdfInstance.text(
        'Reference date: ' + dataDate,
        65,
        12.2
    );
    pdfInstance.text(
        'Information downloaded: ' +
        getLargeDate(Date.now()),
        65,
        15.2
    );
    pdfInstance.text(
        'Source: UN Gender Quota Portal',
        65,
        18.2
    );
    
    return 28;
};

const addPDFTitle = (
    pdfInstance: JsPDF,
    text: string
) => {
    pdfInstance.setFont('Helvetica');
    pdfInstance.setFontSize(18);
    pdfInstance.setTextColor(COLOR_05);
    pdfInstance.text(
        text,
        10,
        28
    );
};

const addPDFSubtitle = (
    pdfInstance: JsPDF,
    text: string, 
    color: string = COLOR_05
) => {
    pdfInstance.setFont('Helvetica', 'normal', 'bold');
    pdfInstance.setFontSize(10);
    pdfInstance.setTextColor(color);
    pdfInstance.text(
        text.toUpperCase(),
        10,
        37
    );
};

const addPDFFooter = (
    pdfInstance: JsPDF,
    pageAndMaxPage: [page: number, maxPage: number]
): void => {
    pdfInstance.setFont('Helvetica', undefined, FONT_WEIGHT_REGULAR);
    pdfInstance.setFontSize(9);
    pdfInstance.setTextColor(COLOR_12);
    pdfInstance.text(
        'Page ' + pageAndMaxPage[0] + ' - ' + pageAndMaxPage[1],
        189,
        273
    );
};

const replaceInvalidCharacters = (text: string | number): string => (
    String(text)
        .replaceAll('<br />', '\n')
);

export const downloadPDFTable = (
    fileName: string,
    dataDate: string,
    tables: PDFTableData[] = []
): void => {
    const pdfInstance = createPDF();

    tables.forEach((tableData, index) => {
        const tableMargins = [
            22.5 + 12 + (tableData.subtitle ? 5 : 0),
            9,
            10]
        const tableWidth = pdfInstance.internal.pageSize.getWidth() - (tableMargins[1] * 2);

        autoTable(pdfInstance, {
            theme: 'plain',
            margin: tableMargins,
            columnStyles: (() => {
                if (tableData.columnSizePercentages) {
                    const result: {[key: number]: {cellWidth: number}} = {};
    
                    tableData.columnSizePercentages.forEach((columnSizePercentage, index) => {
                        result[index] = {
                            cellWidth: (columnSizePercentage / 100) * tableWidth
                        }
                    });
    
                    return result;
                }
            })(),
            head: [tableData.headers.map(headerString => headerString.toUpperCase())],
            body: tableData.rows.map(row => row.map(replaceInvalidCharacters)),
            didDrawPage: () => {
                addPDFHeader(pdfInstance, dataDate);
                addPDFTitle(pdfInstance, tableData.title);
                tableData.subtitle && addPDFSubtitle(pdfInstance, tableData.subtitle);
            },
            didParseCell: data => {
                data.cell.styles.textColor = COLOR_12;
                data.cell.styles.fillColor = '#ffffff';
    
                switch(data.section) {
                    case 'head':
                        data.cell.styles.fontSize = 6;
                        break;
    
                    case 'body':
                        data.cell.styles.fontSize = 7;
                        break;
                }
            },
            willDrawCell: data => {
                if (data.row.section === 'body') {
                    pdfInstance.setDrawColor('#c2c2c2');
                    pdfInstance.setLineWidth(.5);
                    pdfInstance.line(
                        data.cell.x,
                        data.cell.y,
                        data.cell.x + data.cell.width,
                        data.cell.y
                    );
                }
            }
        });

        if (index < tables.length - 1) {
            pdfInstance.addPage();
        }
    });

    const maxPage: number = pdfInstance.getNumberOfPages();

    for (let currentPage = 1; currentPage <= maxPage; currentPage++) {
        pdfInstance.setPage(currentPage);
        addPDFFooter(pdfInstance, [currentPage, maxPage]);
    }

    pdfInstance.save(fileName + '.pdf');
};
